import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ModalShell from '../components/ModalShell';
import LoginForm from '../components/LoginForm';
import { useAuth } from '../context/AuthContext';
import './LandingPage.css';

const useCases = [
  {
    icon: 'fa-share-alt',
    title: 'For traders who share strategies',
    text: 'Package a strategy once, share it with selected investors, and keep the execution logic under one roof.',
  },
  {
    icon: 'fa-check-square-o',
    title: 'For investors who select strategies',
    text: 'Compare strategies, pick the ones that match your risk profile, and monitor them without jumping between portals.',
  },
  {
    icon: 'fa-columns',
    title: 'For managers of multiple broker accounts',
    text: 'Control several accounts from multiple brokers in one place instead of logging into each platform separately.',
  },
];

const benefits = [
  {
    title: 'Unified monitoring',
    text: 'Balances, strategies, and account status stay visible in one workspace.',
  },
  {
    title: 'Faster decisions',
    text: 'You move from review to action without rebuilding context across tabs.',
  },
  {
    title: 'Cleaner collaboration',
    text: 'Traders and investors work from the same operational view.',
  },
  {
    title: 'Broker flexibility',
    text: 'One interface can cover multiple brokers without changing the workflow.',
  },
];

const screenerPoints = [
  {
    title: 'Public strategies only',
    text: 'The screener surfaces strategies that owners have marked public, so users see only shared ideas.',
  },
  {
    title: 'Fast filtering',
    text: 'Search by name, account, exchange, status, profit factor, or recent activity to narrow the list quickly.',
  },
  {
    title: 'One place to review',
    text: 'Open details from the screener and subscribe only after you have access to the strategy.',
  },
];

const previewRows = [
  {
    title: 'Shared trader strategy',
    text: 'Publish a strategy to a selected investor group.',
    tag: 'Shared',
    icon: 'fa-users text-info',
  },
  {
    title: 'Investor selection',
    text: 'Choose strategies and review performance in one place.',
    tag: 'Selected',
    icon: 'fa-line-chart text-success',
  },
  {
    title: 'Multi-broker control',
    text: 'Manage several broker accounts from a single window.',
    tag: 'Unified',
    icon: 'fa-window-restore text-primary',
  },
];

export default function LandingPage() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loginOpen, setLoginOpen] = useState(false);

  useEffect(() => {
    if (!loginOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setLoginOpen(false);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [loginOpen]);

  const primaryAction = (
    <Link to="/screener" className="btn btn-primary btn-lg">
      <i className="fa fa-filter" aria-hidden="true" />
      Go to screener
    </Link>
  );

  const loginAction = !isAuthenticated ? (
    <button type="button" className="btn btn-primary btn-lg" onClick={() => setLoginOpen(true)}>
      <i className="fa fa-sign-in" aria-hidden="true" />
      Sign in
    </button>
  ) : null;

  return (
    <>
      <Header />
      <div className="landing-page">
        <section className="landing-hero container">
        <div className="landing-copy">
          <div className="landing-kicker">Before login</div>
          <h1>One window for multiple accounts.</h1>
          <p className="landing-lead">
            ProfitFactor keeps strategy accounts, investor allocations, and broker balances in one place.
            It is built for traders and investors who need to manage more than one account without switching
            between platforms.
          </p>

          <div className="landing-actions">
            {primaryAction}
            <a className="btn btn-secondary btn-lg" href="#use-cases">
              <i className="fa fa-list" aria-hidden="true" />
              View use cases
            </a>
          </div>

          <div className="landing-metrics" aria-label="Platform highlights">
            <div className="landing-metric">
              <strong>1 view</strong>
              <span>Monitor multiple accounts from one window.</span>
            </div>
            <div className="landing-metric">
              <strong>3 roles</strong>
              <span>Supports traders, investors, and account managers.</span>
            </div>
            <div className="landing-metric">
              <strong>0 tab switching</strong>
              <span>Keep your work aligned while strategies move.</span>
            </div>
          </div>
        </div>

        <div className="landing-preview" aria-label="One window account preview">
          <h2>One window, many accounts</h2>
          <p>Track activity across brokers and strategies from a single operational view.</p>

          <div className="landing-window">
            {previewRows.map((row) => (
              <div className="landing-window__row" key={row.title}>
                <div className="landing-window__body">
                  <strong>{row.title}</strong>
                  <span>{row.text}</span>
                </div>
                <div className="landing-window__tag">{row.tag}</div>
                <div className="landing-window__icon">
                  <i className={`fa ${row.icon}`} aria-hidden="true" />
                </div>
              </div>
            ))}
          </div>
        </div>
        </section>

        <section className="landing-section container" id="use-cases">
          <div className="landing-section__header">
            <h2>Use cases</h2>
            <p>
              The platform is shaped around the workflows that matter before login: strategy distribution,
              investment selection, and multi-account control.
            </p>
          </div>

        <div className="landing-cases">
          {useCases.map((item) => (
            <article className="landing-case" key={item.title}>
              <i className={`fa ${item.icon}`} aria-hidden="true" />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
        </section>

        <section className="landing-section container" id="benefits">
          <div className="landing-section__header">
            <h2>Benefits</h2>
            <p>
              One-window access keeps account activity aligned and reduces the overhead of working across separate
              broker portals.
            </p>
          </div>

        <div className="landing-benefits">
          {benefits.map((item) => (
            <div className="landing-benefit" key={item.title}>
              <strong>{item.title}</strong>
              <span>{item.text}</span>
            </div>
          ))}
        </div>
        </section>

        <section className="landing-section container" id="screener">
          <div className="landing-section__header">
            <h2>Strategy screener</h2>
            <p>
              Review public strategies from across the platform, compare activity, and open strategy details from
              one filtered list.
            </p>
          </div>

        <div className="landing-cases">
          {screenerPoints.map((item) => (
            <article className="landing-case" key={item.title}>
              <i className="fa fa-filter" aria-hidden="true" />
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>

        <div className="landing-screener-cta">
          <div>
            <h3>Open the screener</h3>
            <p>Browse public strategies directly.</p>
          </div>
          {primaryAction}
        </div>
        </section>

        {!isAuthenticated && (
            <section className="landing-cta container">
              <div>
                <h2>Ready to get started?</h2>
                <p>Create an account or use your existing one to enter the workspace.</p>
              </div>
              <div className="landing-cta__actions">
                {loginAction}
                <Link to="/signup" className="btn btn-secondary btn-lg">
                  <i className="fa fa-user-plus" aria-hidden="true" />
                  Create account
                </Link>
              </div>
            </section>
          )}

        <ModalShell
          title="Sign in"
          open={loginOpen}
          onClose={() => setLoginOpen(false)}
        >
          <div className="landing-login-modal">
            <p className="landing-login-modal__lead">Use your existing account to open the workspace.</p>
            <LoginForm
              submitLabel="Open workspace"
              onSuccess={() => {
                setLoginOpen(false);
                navigate('/my/accounts', { replace: true });
              }}
            />
            <p className="landing-login-modal__footer">
              Need an account?{' '}
              <Link to="/signup" onClick={() => setLoginOpen(false)}>
                Create one
              </Link>
            </p>
          </div>
        </ModalShell>
      </div>
    </>
  );
}
